import { useState, useCallback, useRef } from 'react';
import { OPENAI_CHAT_ENDPOINT, getOpenAIHeaders, IS_API_KEY_AVAILABLE } from '../utils/openaiConfig';

/**
 * React hook for handling OpenAI streaming chat completions
 * @param {Object} options - Configuration options
 * @param {string} options.apiKey - OpenAI API key (optional, uses config if not provided)
 * @param {string} options.model - Model to use (defaults to gpt-3.5-turbo)
 * @param {number} options.temperature - Temperature for generation (defaults to 0.7)
 * @param {number} options.maxTokens - Maximum tokens to generate (defaults to 800)
 * @returns {Object} Hook state and functions
 */
export const useStreamingChat = (options = {}) => {
  const {
    apiKey,
    model = 'gpt-3.5-turbo',
    temperature = 0.7,
    maxTokens = 800
  } = options;

  // State management
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamingText, setStreamingText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  
  // Refs for cleanup
  const abortControllerRef = useRef(null);
  const readerRef = useRef(null);

  /**
   * Parse Server-Sent Events data
   * @param {string} data - Raw SSE data
   * @returns {Object|null} Parsed data or null if invalid
   */
  const parseSSEData = useCallback((data) => {
    try {
      // Remove "data: " prefix and parse JSON
      const jsonStr = data.replace(/^data: /, '').trim();
      
      // Handle [DONE] message
      if (jsonStr === '[DONE]') {
        return { done: true };
      }
      
      // Skip empty lines
      if (!jsonStr) {
        return null;
      }
      
      return JSON.parse(jsonStr);
    } catch (err) {
      console.warn('Failed to parse SSE data:', err);
      return null;
    }
  }, []);

  /**
   * Extract content from streaming response
   * @param {Object} parsedData - Parsed SSE data
   * @returns {string} Extracted content
   */
  const extractContent = useCallback((parsedData) => {
    try {
      return parsedData.choices?.[0]?.delta?.content || '';
    } catch (err) {
      console.warn('Failed to extract content:', err);
      return '';
    }
  }, []);

  /**
   * Send streaming chat request
   * @param {Array} messages - Array of message objects with role and content
   * @returns {Promise<void>}
   */
  const sendStreamingMessage = useCallback(async (messages) => {
    // Always send to backend proxy endpoint
    setIsLoading(true);
    setError(null);
    setStreamingText('');
    setIsComplete(false);
    abortControllerRef.current = new AbortController();

    try {
      // Prepare request body
      const requestBody = {
        messages,
        // Optionally add model, temperature, max_tokens if your backend supports it
        model,
        temperature,
        max_tokens: maxTokens
      };

      // Make request to backend
      const response = await fetch('/api/chat/text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`;
        setError(errorMessage);
        throw new Error(errorMessage);
      }

      // Assume backend returns { response: '...' }
      const data = await response.json();
      setStreamingText(data.response || '');
      setIsComplete(true);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'An error occurred during streaming');
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      readerRef.current = null;
    }
  }, [model, temperature, maxTokens]);

  /**
   * Abort current streaming request
   */
  const abortStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (readerRef.current) {
      readerRef.current.cancel();
    }
    setIsLoading(false);
    setError(null);
  }, []);

  /**
   * Reset hook state
   */
  const reset = useCallback(() => {
    abortStream();
    setStreamingText('');
    setError(null);
    setIsLoading(false);
    setIsComplete(false);
  }, [abortStream]);

  /**
   * Send a simple message (convenience function)
   * @param {string} message - User message
   * @param {string} systemPrompt - Optional system prompt
   * @returns {Promise<void>}
   */
  const sendMessage = useCallback(async (message, systemPrompt = '') => {
    const messages = [];
    
    if (systemPrompt) {
      messages.push({
        role: 'system',
        content: systemPrompt
      });
    }
    
    messages.push({
      role: 'user',
      content: message
    });

    await sendStreamingMessage(messages);
  }, [sendStreamingMessage]);

  return {
    // State
    isLoading,
    error,
    streamingText,
    isComplete,
    
    // Functions
    sendStreamingMessage,
    sendMessage,
    abortStream,
    reset
  };
}; 